// /bookings/calendar — Month-grid view of festive bookings (G3.7)
// Keyed by delivery_date. Click a day for a slide-over list.
// RESTAURANT vertical · MANAGER+ · ENABLE_FESTIVE_MODULE.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PageContainer, Select, Skeleton, EmptyState, ErrorBoundary, useToast } from '@bbpos/ui';
import {
  useAllFestivalBookings,
  useFestivalsLookup,
  BookingStatusBadge,
  STATUS_BADGE,
  formatDeliveryTime,
  formatBookingDate,
} from '@/features/bookings';
import type { BookingListItem, BookingStatus } from '@/features/bookings';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise, formatPaiseCompact } from '@/utils/currency';
import { cn, GLASS, GLASS_FILTER, GLASS_INTERACTIVE } from '@/constants/design-tokens';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListIcon,
  XIcon,
} from './icons';

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function sameDay(a: string | Date, b: Date): boolean {
  const ad = new Date(a);
  return (
    ad.getFullYear() === b.getFullYear() &&
    ad.getMonth() === b.getMonth() &&
    ad.getDate() === b.getDate()
  );
}

function buildMonthGrid(year: number, month: number): Date[] {
  // 7 × 6 grid starting Sunday to keep alignment predictable.
  const first = new Date(year, month, 1);
  const startOffset = first.getDay(); // 0 = Sun
  const startDate = new Date(year, month, 1 - startOffset);
  return Array.from({ length: 42 }).map((_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return d;
  });
}

const STATUS_DOT: Record<BookingStatus, string> = {
  PENDING: 'bg-neutral-400',
  CONFIRMED: 'bg-brand-500',
  READY: 'bg-violet-500',
  DELIVERED: 'bg-success-500',
  CANCELLED: 'bg-danger-500',
};

function BookingsCalendarContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  const festivalParam = searchParams.get('festival') ?? '';
  const monthParam = searchParams.get('month') ?? '';
  const statusFilter = searchParams.get('status') ?? '';

  const today = new Date();
  const initialMonth = monthParam
    ? new Date(`${monthParam}-01T00:00:00`)
    : new Date(today.getFullYear(), today.getMonth(), 1);

  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);
  const [drilldownDate, setDrilldownDate] = useState<string | null>(null);

  const { data: festivalsList, isLoading: festivalsLoading } = useFestivalsLookup();
  const festivals = festivalsList ?? [];

  const defaultFestivalId = useMemo(() => {
    if (festivalParam) return festivalParam;
    if (!festivals.length) return '';
    const active = festivals.find((f) => f.status === 'ACTIVE');
    if (active) return active.id;
    return festivals[0]?.id ?? '';
  }, [festivalParam, festivals]);
  const effectiveFestivalId = festivalParam || defaultFestivalId;
  const activeFestival = festivals.find((f) => f.id === effectiveFestivalId);

  // Walk every page — a calendar must reflect all bookings. The API caps
  // ?limit at 100, so a single wide page would undercount busy festivals and
  // drop later dates off the grid (GitHub issue #292).
  const { data, isLoading, error } = useAllFestivalBookings(
    effectiveFestivalId
      ? {
          festival_id: effectiveFestivalId,
          status: (statusFilter as BookingStatus) || undefined,
        }
      : null,
  );

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error, toast]);

  const bookings: BookingListItem[] = data?.data ?? [];
  const totalBookings = data?.total ?? bookings.length;
  // True only when the safety cap in useAllFestivalBookings was hit — the grid
  // is then partial, so surface an honest "showing X of N" notice.
  const isTruncated = totalBookings > bookings.length;

  const grid = useMemo(
    () => buildMonthGrid(viewMonth.getFullYear(), viewMonth.getMonth()),
    [viewMonth],
  );

  // Map yyyy-mm-dd → bookings for that date (within current view window).
  const byDate = useMemo(() => {
    const m = new Map<string, BookingListItem[]>();
    for (const b of bookings) {
      const key = toIsoDate(new Date(b.delivery_date));
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(b);
    }
    return m;
  }, [bookings]);

  // Upcoming deliveries — today onward, soonest first. Independent of the viewed
  // month so staff always see what's next regardless of calendar navigation.
  const upcomingBookings = useMemo(() => {
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return bookings
      .filter((b) => b.status !== 'CANCELLED' && new Date(b.delivery_date) >= startOfToday)
      .sort((a, b) => new Date(a.delivery_date).getTime() - new Date(b.delivery_date).getTime())
      .slice(0, 8);
  }, [bookings, today]);

  const monthLabel = viewMonth.toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });

  function changeMonth(offset: number) {
    const next = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + offset, 1);
    setViewMonth(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('month', toIsoDate(next).slice(0, 7));
      return p;
    });
  }

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, value);
      else p.delete(key);
      return p;
    });
  }

  const drilldownBookings = drilldownDate ? (byDate.get(drilldownDate) ?? []) : [];

  // Daily capacity derived from festival (if set).
  const maxPerDay = (activeFestival as { max_orders_per_day?: number } | undefined)?.max_orders_per_day ?? null;

  if (festivalsLoading) {
    return (
      <>
        <Skeleton variant="rect" height={120} className="rounded-2xl mb-6" />
        <Skeleton variant="rect" height={64} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={560} className="rounded-2xl" />
      </>
    );
  }

  if (festivals.length === 0) {
    return (
      <>
        <header className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 via-indigo-600 to-brand-600 p-6 text-white shadow-[0_20px_60px_-24px_rgba(79,70,229,0.55)] mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">Booking Calendar</h1>
          <p className="text-violet-100 mt-1 text-sm">Monthly overview of festive pre-orders</p>
        </header>
        <EmptyState
          variant="page"
          icon={<CalendarIcon className="h-full w-full" />}
          title="No festivals configured"
          description="Calendar scope is a festival. Configure a festive event first."
          action={
            roleLevel >= ROLE_LEVELS.MANAGER
              ? { label: 'Go to Festive Events', onClick: () => navigate('/festive-events') }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <header className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 via-indigo-600 to-brand-600 p-6 text-white shadow-[0_20px_60px_-24px_rgba(79,70,229,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-black/20 blur-3xl" />
        <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white">Booking Calendar</h1>
            <p className="text-violet-100 mt-1 text-sm">
              {activeFestival?.name ?? 'Select a festival'}
            </p>
          </div>
          <Link
            to={`/bookings${effectiveFestivalId ? `?festival=${effectiveFestivalId}` : ''}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium backdrop-blur-sm transition-colors self-start lg:self-auto"
          >
            <ListIcon className="h-4 w-4" />
            List view
          </Link>
        </div>
      </header>

      {/* Controls */}
      <div className={cn(GLASS_FILTER, 'p-3 mb-4 flex flex-wrap items-end gap-3')}>
        <div className="w-56 min-w-[200px]">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Festival
          </label>
          <Select
            options={festivals.map((f) => ({ value: f.id, label: f.name }))}
            value={effectiveFestivalId}
            onChange={(v) => setParam('festival', typeof v === 'string' ? v : '')}
            placeholder="Select festival"
          />
        </div>
        <div className="w-44">
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Status
          </label>
          <Select
            options={[
              { value: '', label: 'All statuses' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'CONFIRMED', label: 'Confirmed' },
              { value: 'READY', label: 'Ready' },
              { value: 'DELIVERED', label: 'Delivered' },
              { value: 'CANCELLED', label: 'Cancelled' },
            ]}
            value={statusFilter}
            onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
            placeholder="All statuses"
          />
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="p-2 rounded-lg bg-white/70 dark:bg-neutral-800/70 hover:bg-white dark:hover:bg-neutral-800 backdrop-blur-sm transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="min-w-[180px] text-center text-sm font-semibold text-neutral-800 dark:text-neutral-100 font-mono tabular-nums">
            {monthLabel}
          </span>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="p-2 rounded-lg bg-white/70 dark:bg-neutral-800/70 hover:bg-white dark:hover:bg-neutral-800 backdrop-blur-sm transition-colors"
            aria-label="Next month"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              const t = new Date(today.getFullYear(), today.getMonth(), 1);
              setViewMonth(t);
              setParam('month', toIsoDate(t).slice(0, 7));
            }}
            className="ml-2 px-3 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors shadow-sm"
          >
            Today
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-neutral-500 dark:text-neutral-400">
        {(Object.keys(STATUS_BADGE) as BookingStatus[]).map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[k])} />
            {STATUS_BADGE[k].label}
          </span>
        ))}
      </div>

      {/* Partial-data notice — only when the fetch cap was hit */}
      {isTruncated && (
        <div className="mb-3 rounded-xl border border-warning-200 bg-warning-50 dark:border-warning-500/40 dark:bg-warning-500/10 px-4 py-2.5 text-sm text-warning-800 dark:text-warning-200 animate-fade-in">
          Showing {bookings.length.toLocaleString('en-IN')} of{' '}
          {totalBookings.toLocaleString('en-IN')} bookings — the calendar is
          partial. Use the List view to see every booking.
        </div>
      )}

      {/* Calendar grid */}
      <div className={cn(GLASS, 'p-3 animate-fade-in')}>
        <div className="grid grid-cols-7 text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="px-2 py-1 text-center uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {grid.map((d) => {
            const iso = toIsoDate(d);
            const inMonth = d.getMonth() === viewMonth.getMonth();
            const isToday = sameDay(d, today);
            const dayBookings = byDate.get(iso) ?? [];
            const capacity =
              maxPerDay && maxPerDay > 0 ? Math.min(100, Math.round((dayBookings.length / maxPerDay) * 100)) : null;
            const capacityTone =
              capacity === null
                ? 'bg-transparent'
                : capacity >= 80
                  ? 'bg-gradient-to-r from-rose-500 to-danger-500'
                  : capacity >= 50
                    ? 'bg-gradient-to-r from-warning-500 to-amber-400'
                    : 'bg-gradient-to-r from-success-500 to-teal-400';
            return (
              <button
                key={iso}
                type="button"
                onClick={() => dayBookings.length && setDrilldownDate(iso)}
                disabled={dayBookings.length === 0}
                className={cn(
                  'relative rounded-xl p-2 min-h-[96px] text-left transition-all duration-200 backdrop-blur-sm',
                  inMonth
                    ? 'bg-white/80 dark:bg-neutral-900/70'
                    : 'bg-neutral-50/60 dark:bg-neutral-900/30 text-neutral-400',
                  dayBookings.length
                    ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500/50'
                    : 'cursor-default',
                  isToday && 'ring-2 ring-brand-500',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'text-xs font-semibold font-mono tabular-nums',
                      isToday ? 'text-brand-600 dark:text-brand-300' : 'text-neutral-700 dark:text-neutral-200',
                    )}
                  >
                    {d.getDate()}
                  </span>
                  {dayBookings.length > 0 && (
                    <span className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
                      {dayBookings.length}
                    </span>
                  )}
                </div>
                <ul className="mt-1.5 space-y-0.5">
                  {dayBookings.slice(0, 3).map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center gap-1 text-[11px] text-neutral-700 dark:text-neutral-200 truncate"
                    >
                      <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', STATUS_DOT[b.status])} />
                      <span className="truncate">{b.customer_name}</span>
                    </li>
                  ))}
                  {dayBookings.length > 3 && (
                    <li className="text-[10px] font-medium text-brand-600 dark:text-brand-300">
                      +{dayBookings.length - 3} more
                    </li>
                  )}
                </ul>
                {capacity !== null && dayBookings.length > 0 && (
                  <div className="absolute bottom-1 inset-x-2">
                    <div className="h-1 w-full rounded-full bg-neutral-200/70 dark:bg-neutral-700/70 overflow-hidden">
                      <div
                        className={cn('h-full rounded-full', capacityTone)}
                        style={{ width: `${capacity}%` }}
                      />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {isLoading && (
          <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400 text-center">
            Loading bookings…
          </p>
        )}
      </div>

      {/* Upcoming bookings */}
      <div className={cn(GLASS, 'mt-4 p-4 animate-fade-in')}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            Upcoming Bookings
          </h2>
          {upcomingBookings.length > 0 && (
            <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
              Next {upcomingBookings.length}
            </span>
          )}
        </div>
        {upcomingBookings.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No upcoming deliveries for {activeFestival?.name ?? 'this festival'}.
          </p>
        ) : (
          <ul className="space-y-2">
            {upcomingBookings.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/bookings/${b.id}`)}
                  className={cn(
                    GLASS_INTERACTIVE,
                    'w-full text-left p-3 flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-brand-500/50',
                  )}
                >
                  <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', STATUS_DOT[b.status])} />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                      {b.customer_name}
                    </p>
                    <p className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                      {b.booking_number} · {formatBookingDate(b.delivery_date)}
                      {b.delivery_slot ? ` · ${formatDeliveryTime(b.delivery_slot)}` : ''}
                    </p>
                  </div>
                  <BookingStatusBadge status={b.status} />
                  <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100 shrink-0">
                    {formatPaise(b.total_amount, currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Day drill-down */}
      {drilldownDate && (
        <div
          className="fixed inset-0 z-40 flex animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="Day bookings"
        >
          <button
            type="button"
            className="flex-1 bg-neutral-900/40 backdrop-blur-sm"
            onClick={() => setDrilldownDate(null)}
            aria-label="Close drilldown"
          />
          <aside className="w-full sm:w-[420px] bg-white dark:bg-neutral-900 shadow-2xl p-5 overflow-y-auto animate-slide-up">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Deliveries on
                </p>
                <h2 className="text-xl font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100 mt-0.5">
                  {formatBookingDate(drilldownDate)}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setDrilldownDate(null)}
                className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                aria-label="Close"
              >
                <XIcon />
              </button>
            </div>
            {drilldownBookings.length === 0 ? (
              <p className="text-sm text-neutral-500">No bookings for this day.</p>
            ) : (
              <ul className="space-y-2">
                {drilldownBookings.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/bookings/${b.id}`)}
                      className={cn(
                        GLASS_INTERACTIVE,
                        'w-full text-left p-3 focus:outline-none focus:ring-2 focus:ring-brand-500/50',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                            {b.customer_name}
                          </p>
                          <p className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                            {b.booking_number}
                          </p>
                        </div>
                        <BookingStatusBadge status={b.status} />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="text-neutral-500">
                          {b.delivery_slot ? formatDeliveryTime(b.delivery_slot) : 'No time set'} · {b.item_count} items
                        </span>
                        <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                          {formatPaise(b.total_amount, currency)}
                        </span>
                      </div>
                      {b.balance_due > 0 && (
                        <p className="mt-1 text-xs text-warning-600 dark:text-warning-300 font-mono tabular-nums">
                          Balance due {formatPaiseCompact(b.balance_due, currency)}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

export function BookingsCalendarPage() {
  return (
    <PageContainer title="Booking Calendar" breadcrumbs={[{ label: 'Bookings', href: '/bookings' }, { label: 'Calendar' }]}>
      <ErrorBoundary>
        <BookingsCalendarContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
